import { FunctionComponent, ReactElement, useEffect, useRef } from 'react';
import { Snackbar, Alert, AlertTitle, Portal, IconButton } from '@mui/material';
import useToastMessage from '@/shared/context/toast-message-context';
import CloseIcon from '@mui/icons-material/Close';

const DEFAULT_MESSAGE_DURATION = 6000;

const ToastMessageContainer: FunctionComponent = (): ReactElement => {
	const { open, onClose, message, position } = useToastMessage();
	const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	const containerRef = useRef<HTMLElement>(document.getElementById('toast-message') as HTMLElement);

	const handleClose = (_: React.SyntheticEvent | Event, reason?: string) => {
		if (reason !== 'clickaway') onClose();
	};

	useEffect(() => {
		if (!message || message.disableSelfClosing) return undefined;

		if (timerRef.current) clearTimeout(timerRef.current);
		timerRef.current = setTimeout(() => onClose(), message.duration ?? DEFAULT_MESSAGE_DURATION);

		return () => {
			if (timerRef.current) clearTimeout(timerRef.current);
		};
	}, [message, onClose]);

	const closeAction = (
		<IconButton size="small" aria-label="close" color="inherit" onClick={handleClose}>
			<CloseIcon fontSize="small" />
		</IconButton>
	);

	return (
		<Portal container={containerRef.current}>
			<Snackbar open={open} anchorOrigin={position} onClose={handleClose}>
				<Alert
					variant="filled"
					severity={message?.type}
					action={message?.showCloseButton && closeAction}
				>
					{message?.title && <AlertTitle>{message?.title}</AlertTitle>}
					{message?.content}
				</Alert>
			</Snackbar>
		</Portal>
	);
};

export default ToastMessageContainer;
